import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

interface MonitorInfo {
  id: number;
  email: string;
  full_name: string;
  phone: string;
  role: string;
  is_active: boolean;
  current_shift: boolean;
  assigned_store_count: number;
}

interface Assignment {
  id: number;
  name: string;
  address: string;
  assigned_at: string;
}

interface AdminStore {
  id: number;
  name: string;
  address: string;
  assigned_monitor_count: number;
  latest_alert_at: string | null;
}

export default function MonitorDetailPage() {
  const { monitorId } = useParams<{ monitorId: string }>();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [monitor, setMonitor] = useState<MonitorInfo | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [allStores, setAllStores] = useState<AdminStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<number | ''>('');
  const [assigning, setAssigning] = useState(false);
  const [removingStoreId, setRemovingStoreId] = useState<number | null>(null);
  const [roleUpdating, setRoleUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [monRes, assignRes, storeRes] = await Promise.all([
        api.get('/admin/monitors'),
        api.get(`/admin/monitors/${monitorId}/assignments`),
        api.get('/admin/stores'),
      ]);
      const monitors = monRes.data.monitors as MonitorInfo[];
      const found = monitors.find((m) => m.id === Number(monitorId));
      setMonitor(found || null);
      console.log('assignments response:', JSON.stringify(assignRes.data.assignments, null, 2));
      setAssignments(assignRes.data.assignments);
      setAllStores(storeRes.data.stores);
      setLoading(false);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load monitor data.'));
      setLoading(false);
    }
  }, [monitorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-dismiss action messages
  useEffect(() => {
    if (actionMsg) {
      const t = setTimeout(() => setActionMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [actionMsg]);

  const assignedStoreIds = new Set(assignments.map((a) => a.id));
  const availableStores = allStores.filter((s) => !assignedStoreIds.has(s.id));

  const handleAssign = async () => {
    if (!selectedStoreId) return;
    setAssigning(true);
    setActionMsg(null);
    try {
      const url = '/admin/assignments';
      const body = {
        monitor_id: Number(monitorId),
        store_id: Number(selectedStoreId),
      };
      console.log('[handleAssign] URL:', api.defaults.baseURL + url);
      console.log('[handleAssign] Request body:', JSON.stringify(body));
      console.log('[handleAssign] monitor_id:', body.monitor_id, '| typeof:', typeof body.monitor_id);
      console.log('[handleAssign] store_id:', body.store_id, '| typeof:', typeof body.store_id);
      await api.post(url, body);
      setSelectedStoreId('');
      setActionMsg({ type: 'success', text: 'Store assigned successfully.' });
      await fetchData();
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: extractErrorMessage(err, 'Failed to assign store.') });
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (storeId: number, storeName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${storeName} from this monitor?`)) return;
    setRemovingStoreId(storeId);
    setActionMsg(null);
    try {
      await api.delete(`/admin/assignments?monitor_id=${Number(monitorId)}&store_id=${storeId}`);
      setActionMsg({ type: 'success', text: 'Store removed successfully.' });
      await fetchData();
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: extractErrorMessage(err, 'Failed to remove assignment.') });
    } finally {
      setRemovingStoreId(null);
    }
  };

  const handleRoleChange = async (newRole: string) => {
    if (!monitor || monitor.role === newRole) return;
    setRoleUpdating(true);
    setActionMsg(null);
    try {
      await api.put(`/admin/monitors/${monitorId}/role`, { role: newRole });
      setActionMsg({ type: 'success', text: `Role updated to ${newRole}.` });
      await fetchData();
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: extractErrorMessage(err, 'Failed to update role.') });
    } finally {
      setRoleUpdating(false);
    }
  };

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.accessDenied}>
            <span style={styles.accessDeniedIcon}>&#9888;</span>
            <h2 style={{ margin: '0 0 8px', fontSize: 'var(--text-xl)', fontFamily: 'var(--font-display)', color: 'var(--status-critical)' }}>Access Denied</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' }}>Lead or Admin role required to view this page.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate('/admin')} style={styles.backBtn}>
          &#8249; Back to Admin
        </button>

        {loading && <p style={styles.statusText}>Loading monitor details...</p>}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {!loading && !error && monitor && (
          <>
            {/* Monitor info card */}
            <div style={styles.infoCard}>
              <div style={styles.infoCardHeader}>
                <div>
                  <h2 style={styles.monitorName}>{monitor.full_name}</h2>
                  <div style={styles.monitorEmail}>{monitor.email}</div>
                  {monitor.phone && <div style={styles.monitorPhone}>{monitor.phone}</div>}
                </div>
                <div style={styles.infoCardRight}>
                  <div style={styles.shiftStatusRow}>
                    <span style={{
                      ...styles.shiftDot,
                      background: monitor.current_shift ? 'var(--status-success)' : 'var(--text-tertiary)',
                      boxShadow: monitor.current_shift ? '0 0 6px rgba(23,178,106,0.5)' : 'none',
                    }} />
                    <span style={{ fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: monitor.current_shift ? 'var(--status-success)' : 'var(--text-secondary)' }}>
                      {monitor.current_shift ? 'On Shift' : 'Off Shift'}
                    </span>
                  </div>
                  <span style={roleBadgeStyle(monitor.role)}>{monitor.role}</span>
                </div>
              </div>

              {/* Role change (only for admin role users) */}
              {user?.role === 'admin' && (
                <div style={styles.roleChangeRow}>
                  <span style={{ fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginRight: 10 }}>Change role:</span>
                  {['monitor', 'lead', 'admin'].map((r) => (
                    <button
                      key={r}
                      onClick={() => handleRoleChange(r)}
                      disabled={monitor.role === r || roleUpdating}
                      style={{
                        ...styles.roleBtn,
                        ...(monitor.role === r ? styles.roleBtnActive : {}),
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Action message */}
            {actionMsg && (
              <div style={actionMsg.type === 'success' ? styles.successBanner : styles.errorBanner}>
                {actionMsg.text}
              </div>
            )}

            {/* View Performance link */}
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={() => navigate(`/admin/monitors/${monitorId}/performance`)}
                style={styles.perfBtn}
              >
                View Performance &#8594;
              </button>
            </div>

            {/* Assigned stores */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                Assigned Stores ({assignments.length})
              </h3>
              {assignments.length === 0 && (
                <p style={styles.statusText}>No stores assigned to this monitor.</p>
              )}
              {assignments.map((a) => (
                <div key={a.id} style={styles.assignmentItem}>
                  <div style={styles.assignmentInfo}>
                    <div style={styles.assignmentName}>
                      {a.name}{a.address ? ` — ${a.address}` : ''}
                    </div>
                    <div style={styles.assignmentAddr}>
                      Assigned {new Date(a.assigned_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(a.id, a.name)}
                    disabled={removingStoreId === a.id}
                    style={styles.removeBtn}
                  >
                    {removingStoreId === a.id ? '...' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>

            {/* Add store */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Add Store Assignment</h3>
              {availableStores.length === 0 ? (
                <p style={styles.statusText}>All stores are already assigned to this monitor.</p>
              ) : (
                <div style={styles.addRow}>
                  <select
                    value={selectedStoreId}
                    onChange={(e) => setSelectedStoreId(e.target.value ? Number(e.target.value) : '')}
                    style={styles.select}
                  >
                    <option value="">Select a store...</option>
                    {availableStores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} — {s.address}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssign}
                    disabled={!selectedStoreId || assigning}
                    style={{
                      ...styles.assignBtn,
                      ...(!selectedStoreId ? styles.assignBtnDisabled : {}),
                    }}
                  >
                    {assigning ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function roleBadgeStyle(role: string): React.CSSProperties {
  const map: Record<string, { bg: string; color: string }> = {
    admin: { bg: 'var(--accent-muted)', color: 'var(--accent-text)' },
    lead: { bg: 'var(--status-warning-muted)', color: 'var(--status-warning)' },
    monitor: { bg: 'var(--status-neutral-muted)', color: 'var(--text-secondary)' },
  };
  const colors = map[role] || map.monitor;
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    background: colors.bg,
    color: colors.color,
    textTransform: 'capitalize',
  };
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 720,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  backBtn: {
    display: 'inline-block',
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 'var(--space-5)',
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
    marginBottom: 'var(--space-4)',
  },
  successBanner: {
    background: 'var(--status-success-muted)',
    color: 'var(--status-success)',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(23,178,106,0.3)',
    marginBottom: 'var(--space-4)',
  },
  accessDenied: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid rgba(255,71,87,0.3)',
    padding: '40px 32px',
    textAlign: 'center',
  },
  accessDeniedIcon: {
    fontSize: 36,
    color: 'var(--status-critical)',
    display: 'block',
    marginBottom: 'var(--space-3)',
  },

  // Info card
  infoCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '22px 24px',
    marginBottom: 'var(--space-6)',
  },
  infoCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  infoCardRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
  },
  monitorName: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 4px',
  },
  monitorEmail: {
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
  monitorPhone: {
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-tertiary)',
    marginTop: 2,
  },
  shiftStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  shiftDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  roleChangeRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'var(--space-4)',
    paddingTop: 'var(--space-4)',
    borderTop: '1px solid var(--border-subtle)',
  },
  roleBtn: {
    padding: '4px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    marginRight: 6,
    textTransform: 'capitalize',
  },
  roleBtnActive: {
    background: 'var(--accent)',
    borderColor: 'var(--accent)',
    color: '#fff',
    cursor: 'default',
  },

  // Sections
  section: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid var(--border-subtle)',
    padding: '20px 24px',
    marginBottom: 'var(--space-5)',
  },
  sectionTitle: {
    fontSize: 'var(--text-lg)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: '0 0 14px',
  },

  // Assignment items
  assignmentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid var(--border-subtle)',
  },
  assignmentInfo: {
    flex: 1,
    minWidth: 0,
  },
  assignmentName: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    marginBottom: 2,
  },
  assignmentAddr: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
  },
  metaSep: {
    margin: '0 6px',
  },
  removeBtn: {
    padding: '5px 14px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'transparent',
    color: 'var(--status-critical)',
    fontSize: 'var(--text-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    flexShrink: 0,
    marginLeft: 12,
  },

  // Add store row
  addRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  select: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    background: 'var(--input-bg)',
    colorScheme: 'dark',
  },
  assignBtn: {
    padding: '8px 20px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  assignBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  perfBtn: {
    padding: '10px 20px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center' as const,
  },
};
